import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useGetCrmFilterFieldsQuery } from "store/apis/crmFields";
import { useState } from "react";
import { CrmFieldFilter, CrmFilterField } from "types/Models/crmFields";
import { isCrmFilterComplete } from "util/crmFilter/isCrmFilterComplete";
import { CrmConditionRow } from "./CrmConditionRow";

type CrmFieldFilterModalProps = {
  isOpen: boolean;
  onClose: () => void;
  appliedFilters: CrmFieldFilter[];
  onApply: (filters: CrmFieldFilter[]) => void;
};

const EMPTY_FIELDS: CrmFilterField[] = [];
const NO_INITIAL_VALUES = {};
const SELECT_PORTAL_TARGET = document.body;

const noop = () => {};

export const CrmFieldFilterModal = ({
  isOpen,
  onClose,
  appliedFilters,
  onApply,
}: CrmFieldFilterModalProps) => {
  const [draftFilters, setDraftFilters] =
    useState<CrmFieldFilter[]>(appliedFilters);

  const { data, isLoading, isSuccess, isError } = useGetCrmFilterFieldsQuery(
    undefined,
    { skip: !isOpen }
  );

  const fields = data || EMPTY_FIELDS;
  const fieldByKey = new Map(fields.map((field) => [field.field_key, field]));
  const usedFieldKeys = new Set(draftFilters.map(({ field_key }) => field_key));

  const availableOptions = fields
    .filter(({ field_key }) => !usedFieldKeys.has(field_key))
    .map(({ field_key, label }) => ({ value: field_key, label }));

  const addCondition = (fieldKey: string) => {
    setDraftFilters((current) => [...current, { field_key: fieldKey }]);
  };

  const handleAddField = (option: unknown) => {
    if (option && typeof option === "object" && "value" in option) {
      addCondition(String(option.value));
    }
  };

  const updateCondition = (fieldKey: string) => (condition: CrmFieldFilter) => {
    setDraftFilters((current) =>
      current.map((filter) =>
        filter.field_key === fieldKey ? condition : filter
      )
    );
  };

  const removeCondition = (fieldKey: string) => {
    setDraftFilters((current) =>
      current.filter((filter) => filter.field_key !== fieldKey)
    );
  };

  const clearAll = () => {
    setDraftFilters([]);
  };

  // A condition with no real operand value yet (blank text, NaN number, a multi-select
  // match picked with no values) would be rejected by the backend and fail the whole group
  // list fetch, so it is dropped on apply rather than blocking the whole submit
  const completeFilters = draftFilters.filter(isCrmFilterComplete);

  const handleApply = () => {
    onApply(completeFilters);
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      return;
    }

    setDraftFilters(appliedFilters);
    onClose();
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Modal.Content size="xl" useCustomOverlay>
        <Modal.Header>
          <Modal.Title>Advanced filters</Modal.Title>
          <Modal.Close />
        </Modal.Header>

        <Modal.Body>
          <Async.Root
            isLoading={isLoading}
            isSuccess={isSuccess}
            isError={isError}
            isEmpty={fields.length === 0}
          >
            <Async.Empty>
              <div className="t-text-body-sm t-text-text-30 t-py-6 t-text-center">
                No filterable CRM fields are configured yet.
              </div>
            </Async.Empty>

            <Async.ErrorHandler>
              <div className="t-text-body-sm t-text-text-30 t-py-6 t-text-center">
                Could not load CRM fields. Please try again.
              </div>
            </Async.ErrorHandler>

            <Async.Success>
              <FormikForm initialValues={NO_INITIAL_VALUES} onSubmit={noop}>
                <div className="t-flex t-flex-col t-gap-4">
                  {draftFilters.length === 0 && (
                    <div className="t-text-body-sm t-text-text-30">
                      Add a field below to filter groups by their CRM values.
                    </div>
                  )}

                  {draftFilters.map((filter) => {
                    const field = fieldByKey.get(filter.field_key);

                    if (!field) {
                      return null;
                    }

                    return (
                      <CrmConditionRow
                        key={filter.field_key}
                        field={field}
                        condition={filter}
                        onChange={updateCondition(filter.field_key)}
                        onRemove={removeCondition}
                      />
                    );
                  })}

                  <div className="t-w-72">
                    <Combobox
                      withForm={false}
                      size="small"
                      name="add-crm-field"
                      placeholder="Add a field"
                      menuPortalTarget={SELECT_PORTAL_TARGET}
                      value={null}
                      options={availableOptions}
                      onChange={handleAddField}
                    />
                  </div>
                </div>
              </FormikForm>
            </Async.Success>
          </Async.Root>
        </Modal.Body>

        <Modal.Footer>
          <div className="t-flex t-justify-between t-items-center t-w-full">
            <Button type="button" customType="link" onClick={clearAll}>
              Clear all
            </Button>

            <div className="t-flex t-gap-3">
              <Button type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button type="button" customType="primary" onClick={handleApply}>
                Apply
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
